import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { ANALYTICS_EVENTS } from '@/helpers/analyticsEvents';
import { usePostHogIdentification } from '@/hooks/usePostHogIdentification';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import Alert, { AlertType } from '@/components/ui/Alert';
import styles from './AuthPages.module.scss';

const ForgotPasswordComponent = () => {
  const { t } = useTranslation();
  const { forgotPassword } = useAuthContext();
  const { captureEvent } = usePostHogIdentification();
  const [formData, setFormData] = useState({ email: '' });
  const [errors, setErrors] = useState<{ email?: string }>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{ message: string; type: AlertType }>({ message: '', type: 'info' });

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};
    if (!formData.email) {
      newErrors.email = t('email') + ' ' + t('required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('email') + ' ' + t('invalid');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
    setAlertMessage({ message: '', type: 'info' });

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await forgotPassword(formData.email);
      captureEvent(ANALYTICS_EVENTS.PASSWORD_RESET_REQUESTED);
      setAlertMessage({ message: t('emailForResetPasswordSent'), type: 'success' });
    } catch (error: unknown) {
      setAlertMessage({ message: (error as Error).message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <form onSubmit={handleSubmit} className={styles.form} aria-labelledby="forgot-password-heading">
        {/* כותרת h1 לעמוד - ללא כותרת ראשית קורא מסך לא יודע היכן הוא נמצא.
            sr-only כדי לא לשנות את העיצוב הקיים. */}
        <h1 id="forgot-password-heading" className="sr-only">
          {t('forgotPassword')}
        </h1>

        {alertMessage.message && <Alert type={alertMessage.type} message={alertMessage.message} />}

        <div className={styles.field}>
          <Input
            type="email"
            name="email"
            placeholder={t('email')}
            /* placeholder נעלם ברגע שמקלידים - בלי aria-label לשדה אין תווית */
            aria-label={t('email')}
            value={formData.email}
            onChange={handleChange}
            error={submitted && !!errors.email}
            aria-describedby={submitted && errors.email ? 'forgot-password-email-error' : undefined}
            autoComplete="email"
            autoFocus
          />
          {/* role="alert" - שגיאת ולידציה חייבת להיות מוקראת, לא רק מוצגת */}
          {submitted && errors.email && (
            <p id="forgot-password-email-error" role="alert" className={`${styles.fieldError} body-sm-regular`}>
              {errors.email}
            </p>
          )}
        </div>

        <div className={styles.buttonsContainer}>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? (
              <>
                <span className={styles.spinner}></span> {t('sending')}
              </>
            ) : (
              t('submit')
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ForgotPasswordComponent;
